import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Inbox,
  Newspaper,
  Trash2,
  Pencil,
  Plus,
  LogOut,
  Loader2,
  Eye,
  EyeOff,
  Save,
  X,
  Download,
  CheckCircle2,
  Circle,
  Search,
  Mail,
  Building2,
  Tag,
  FileText,
  ExternalLink,
  ArrowUpRight,
  Hexagon,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "medhive_admin_token";

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` });
const fmt = (iso) => new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-US", { dateStyle: "medium" });

const emptyPost = { title: "", excerpt: "", content: "", thumbnail: "", external_url: "", published: true };

const inputCls = "h-11 rounded-xl border-[#E5DCC9] bg-[#FDFBF7] text-[#1F1B13] placeholder:text-[#B8AE9C] focus-visible:ring-[#E8930C]/50";

/* ---------- small pieces ---------- */
const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className="rounded-2xl border border-[#EDE5D3] bg-white p-5 shadow-[0_2px_10px_rgba(31,27,19,0.03)]">
    <div className="flex items-center justify-between">
      <span className="text-xs font-semibold uppercase tracking-[0.12em] text-[#8A8272]">{label}</span>
      <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${accent ? "bg-[#F0A322]/15 text-[#B45309]" : "bg-[#FAF6EE] text-[#8A8272]"}`}>
        <Icon className="h-4 w-4" />
      </span>
    </div>
    <p className="mt-2 font-heading text-3xl font-bold text-[#1F1B13]">{value}</p>
  </div>
);

const SideItem = ({ active, onClick, icon: Icon, children, badge, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`group flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold transition-colors ${
      active ? "bg-[#F0A322] text-[#231A07]" : "text-[#C9BFA8] hover:bg-white/5 hover:text-white"
    }`}
  >
    <Icon className="h-4 w-4 shrink-0" />
    <span className="flex-1 text-left">{children}</span>
    {badge > 0 && (
      <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${active ? "bg-[#231A07]/15 text-[#231A07]" : "bg-[#F0A322]/20 text-[#F0A322]"}`}>
        {badge}
      </span>
    )}
  </button>
);

const InterestBadge = ({ value }) =>
  value ? (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F0A322]/12 px-3 py-1 text-xs font-semibold text-[#B45309]">
      <Tag className="h-3 w-3" /> {value}
    </span>
  ) : null;

const IconBtn = ({ onClick, label, testId, title, danger, children }) => (
  <button
    onClick={onClick}
    aria-label={label}
    data-testid={testId}
    title={title || label}
    className={`rounded-full border border-[#EDE5D3] p-2 text-[#8A8272] transition-colors ${
      danger ? "hover:border-red-300 hover:bg-red-50 hover:text-red-500" : "hover:border-[#E8930C] hover:bg-[#F0A322]/10 hover:text-[#B45309]"
    }`}
  >
    {children}
  </button>
);

const Admin = () => {
  const [authed, setAuthed] = useState(!!localStorage.getItem(TOKEN_KEY));
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [leadFilter, setLeadFilter] = useState("all"); // all | new | handled

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => document.head.removeChild(meta);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
  }, []);

  const loadAll = useCallback(async () => {
    try {
      const [l, b] = await Promise.all([
        axios.get(`${API}/contact`, { headers: authHeaders() }),
        axios.get(`${API}/admin/blogs`, { headers: authHeaders() }),
      ]);
      setLeads(l.data);
      setPosts(b.data);
    } catch (e) {
      if (e.response?.status === 401) {
        toast.error("Session expired — please log in again.");
        logout();
      }
    }
  }, [logout]);

  useEffect(() => {
    if (authed) loadAll();
  }, [authed, loadAll]);

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      setAuthed(true);
      setPassword("");
      toast.success("Welcome back.");
    } catch {
      toast.error("Incorrect password.");
    } finally {
      setLoggingIn(false);
    }
  };

  const exportCsv = () => {
    const header = ["Date", "Name", "Email", "Company", "Interest", "Message", "Handled"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = leads.map((l) =>
      [fmt(l.created_at), l.name, l.email, l.company, l.interest, l.message, l.handled ? "Yes" : "No"].map(esc).join(",")
    );
    const blob = new Blob([[header.map(esc).join(","), ...rows].join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `medhive-enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const toggleHandled = async (lead) => {
    try {
      await axios.patch(`${API}/contact/${lead.id}`, { handled: !lead.handled }, { headers: authHeaders() });
      setLeads((ls) => ls.map((l) => (l.id === lead.id ? { ...l, handled: !l.handled } : l)));
    } catch {
      toast.error("Could not update lead.");
    }
  };

  const deleteLead = async (id) => {
    try {
      await axios.delete(`${API}/contact/${id}`, { headers: authHeaders() });
      setLeads((ls) => ls.filter((l) => l.id !== id));
      toast.success("Lead deleted.");
    } catch {
      toast.error("Could not delete lead.");
    }
  };

  const savePost = async (e) => {
    e.preventDefault();
    if (!editing.title.trim()) {
      toast.error("Title is required.");
      return;
    }
    setSaving(true);
    try {
      if (editing.id) {
        const { id, slug, created_at, updated_at, ...body } = editing;
        await axios.put(`${API}/blogs/${id}`, body, { headers: authHeaders() });
        toast.success("Post updated.");
      } else {
        await axios.post(`${API}/blogs`, editing, { headers: authHeaders() });
        toast.success("Post created.");
      }
      setEditing(null);
      loadAll();
    } catch {
      toast.error("Could not save post.");
    } finally {
      setSaving(false);
    }
  };

  const deletePost = async (id) => {
    try {
      await axios.delete(`${API}/blogs/${id}`, { headers: authHeaders() });
      setPosts((ps) => ps.filter((p) => p.id !== id));
      toast.success("Post deleted.");
    } catch {
      toast.error("Could not delete post.");
    }
  };

  const togglePublish = async (post) => {
    try {
      await axios.put(`${API}/blogs/${post.id}`, { published: !post.published }, { headers: authHeaders() });
      loadAll();
    } catch {
      toast.error("Could not update post.");
    }
  };

  const newLeads = leads.filter((l) => !l.handled).length;
  const drafts = posts.filter((p) => !p.published).length;

  const visibleLeads = useMemo(() => {
    const q = search.trim().toLowerCase();
    return leads.filter((l) => {
      if (leadFilter === "new" && l.handled) return false;
      if (leadFilter === "handled" && !l.handled) return false;
      if (!q) return true;
      return [l.name, l.email, l.company, l.interest, l.message].some((v) => (v || "").toLowerCase().includes(q));
    });
  }, [leads, search, leadFilter]);

  /* ---------- login screen ---------- */
  if (!authed) {
    return (
      <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#1F1B13] px-6" data-testid="admin-login">
        <Hexagon className="pointer-events-none absolute -left-20 -top-20 h-96 w-96 text-[#F0A322]/[0.06]" strokeWidth={0.6} />
        <Hexagon className="pointer-events-none absolute -bottom-24 -right-16 h-[28rem] w-[28rem] text-[#F0A322]/[0.06]" strokeWidth={0.6} />
        <form onSubmit={login} className="relative w-full max-w-sm rounded-3xl border border-white/10 bg-white p-8 shadow-[0_24px_80px_rgba(0,0,0,0.45)]">
          <img src="/assets/logo-hex-hd.png" alt="MedHive" className="mx-auto h-14 w-auto" />
          <h1 className="mt-5 text-center font-heading text-2xl font-bold text-[#1F1B13]">Admin Console</h1>
          <p className="mt-1.5 text-center text-sm text-[#8A8272]">Manage blog posts & enquiries</p>
          <div className="mt-7 space-y-2">
            <Label htmlFor="admin-pass" className="text-[#4A4436]">Password</Label>
            <Input
              id="admin-pass"
              type="password"
              data-testid="admin-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter admin password"
              className={inputCls}
            />
          </div>
          <button
            type="submit"
            disabled={loggingIn}
            data-testid="admin-login-btn"
            className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#F0A322] px-6 py-3 text-sm font-semibold text-[#231A07] transition-colors hover:bg-[#E8930C] disabled:opacity-60"
          >
            {loggingIn && <Loader2 className="h-4 w-4 animate-spin" />} Sign in
          </button>
          <Link to="/" className="mt-5 flex items-center justify-center gap-1 text-xs font-semibold text-[#8A8272] hover:text-[#B45309]">
            ← Back to website
          </Link>
        </form>
      </div>
    );
  }

  /* ---------- dashboard ---------- */
  return (
    <div className="flex min-h-screen bg-[#FAF6EE]" data-testid="admin-dashboard">
      {/* sidebar */}
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col bg-[#1F1B13] px-4 py-6 lg:flex">
        <Link to="/" className="flex items-center gap-3 px-2">
          <img src="/assets/logo-hex-hd.png" alt="MedHive" className="h-9 w-auto" />
          <span className="font-heading text-lg font-bold text-white">
            Med<span className="text-[#F0A322]">Hive</span>
          </span>
        </Link>
        <p className="mt-1 px-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#8A8272]">Admin Console</p>
        <nav className="mt-8 space-y-1.5">
          <SideItem active={tab === "leads"} onClick={() => { setTab("leads"); setEditing(null); }} icon={Inbox} badge={newLeads} testId="tab-leads">
            Enquiries
          </SideItem>
          <SideItem active={tab === "blogs"} onClick={() => setTab("blogs")} icon={Newspaper} badge={0} testId="tab-blogs">
            Blog Posts
          </SideItem>
        </nav>
        <div className="mt-auto space-y-1.5 border-t border-white/10 pt-4">
          <Link to="/" className="flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold text-[#C9BFA8] transition-colors hover:bg-white/5 hover:text-white">
            <ExternalLink className="h-4 w-4" /> View website
          </Link>
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold text-[#C9BFA8] transition-colors hover:bg-white/5 hover:text-white"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* main */}
      <div className="min-w-0 flex-1">
        {/* mobile top bar */}
        <div className="sticky top-0 z-20 flex items-center justify-between gap-3 bg-[#1F1B13] px-4 py-3 lg:hidden">
          <div className="flex items-center gap-2">
            <img src="/assets/logo-hex-hd.png" alt="MedHive" className="h-7 w-auto" />
            <span className="font-heading text-sm font-bold text-white">Admin</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setTab("leads"); setEditing(null); }}
              data-testid="tab-leads-mobile"
              className={`rounded-full px-4 py-1.5 text-xs font-semibold ${tab === "leads" ? "bg-[#F0A322] text-[#231A07]" : "text-[#C9BFA8]"}`}
            >
              Enquiries
            </button>
            <button
              onClick={() => setTab("blogs")}
              data-testid="tab-blogs-mobile"
              className={`rounded-full px-4 py-1.5 text-xs font-semibold ${tab === "blogs" ? "bg-[#F0A322] text-[#231A07]" : "text-[#C9BFA8]"}`}
            >
              Posts
            </button>
            <button onClick={logout} aria-label="Sign out" className="p-1.5 text-[#C9BFA8]">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>

        <main className="mx-auto max-w-5xl px-5 py-8 lg:px-10 lg:py-10">
          {/* header + stats */}
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h1 className="font-heading text-2xl font-bold text-[#1F1B13] lg:text-3xl">
                {tab === "leads" ? "Enquiries" : editing ? (editing.id ? "Edit Post" : "New Post") : "Blog Posts"}
              </h1>
              <p className="mt-1 text-sm text-[#8A8272]">
                {tab === "leads" ? "Contact form submissions, newest first." : "Create, edit and publish articles on the public blog."}
              </p>
            </div>
            {tab === "leads" && leads.length > 0 && (
              <button
                onClick={exportCsv}
                data-testid="leads-export-csv"
                className="inline-flex items-center gap-2 rounded-full border border-[#D9D0BC] bg-white px-5 py-2.5 text-sm font-semibold text-[#5D564A] transition-colors hover:border-[#E8930C] hover:text-[#B45309]"
              >
                <Download className="h-4 w-4" /> Export CSV
              </button>
            )}
            {tab === "blogs" && !editing && (
              <button
                onClick={() => setEditing({ ...emptyPost })}
                data-testid="new-post-btn"
                className="inline-flex items-center gap-2 rounded-full bg-[#F0A322] px-6 py-2.5 text-sm font-semibold text-[#231A07] transition-colors hover:bg-[#E8930C]"
              >
                <Plus className="h-4 w-4" /> New Post
              </button>
            )}
          </div>

          {!editing && (
            <div className="mt-6 grid grid-cols-2 gap-4 xl:grid-cols-4">
              <StatCard icon={Inbox} label="Enquiries" value={leads.length} />
              <StatCard icon={Mail} label="Awaiting reply" value={newLeads} accent />
              <StatCard icon={Newspaper} label="Published posts" value={posts.length - drafts} />
              <StatCard icon={FileText} label="Drafts" value={drafts} />
            </div>
          )}

          {/* ---------- LEADS ---------- */}
          {tab === "leads" && (
            <div className="mt-6" data-testid="leads-list">
              <div className="flex flex-wrap items-center gap-3">
                <div className="relative min-w-[220px] flex-1">
                  <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#B8AE9C]" />
                  <Input
                    data-testid="leads-search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search name, email, company…"
                    className={`${inputCls} pl-10`}
                  />
                </div>
                <div className="flex gap-1.5 rounded-full border border-[#E5DCC9] bg-white p-1">
                  {[
                    ["all", "All"],
                    ["new", "New"],
                    ["handled", "Handled"],
                  ].map(([k, label]) => (
                    <button
                      key={k}
                      onClick={() => setLeadFilter(k)}
                      data-testid={`leads-filter-${k}`}
                      className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${
                        leadFilter === k ? "bg-[#1F1B13] text-white" : "text-[#8A8272] hover:text-[#1F1B13]"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-5 space-y-4">
                {visibleLeads.length === 0 && (
                  <div className="rounded-2xl border border-dashed border-[#D9D0BC] bg-white/60 p-12 text-center" data-testid="leads-empty">
                    <Inbox className="mx-auto h-8 w-8 text-[#C9BFA8]" />
                    <p className="mt-3 text-sm font-semibold text-[#5D564A]">
                      {leads.length === 0 ? "No enquiries yet" : "No enquiries match your filters"}
                    </p>
                    <p className="mt-1 text-xs text-[#8A8272]">
                      {leads.length === 0 ? "Contact form submissions will appear here." : "Try clearing the search or filter."}
                    </p>
                  </div>
                )}
                {visibleLeads.map((l) => (
                  <div
                    key={l.id}
                    className={`rounded-2xl border bg-white p-6 shadow-[0_2px_10px_rgba(31,27,19,0.03)] transition-opacity ${
                      l.handled ? "border-emerald-200/80 opacity-75" : "border-[#EDE5D3]"
                    }`}
                    data-testid="lead-card"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="flex items-start gap-4">
                        <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl font-heading text-sm font-bold ${l.handled ? "bg-emerald-50 text-emerald-600" : "bg-[#F0A322]/15 text-[#B45309]"}`}>
                          {(l.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="font-heading text-base font-semibold text-[#1F1B13]">{l.name}</p>
                            <InterestBadge value={l.interest} />
                            {l.handled && (
                              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-bold text-emerald-600">
                                <CheckCircle2 className="h-3 w-3" /> Handled
                              </span>
                            )}
                          </div>
                          <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[#8A8272]">
                            <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1.5 hover:text-[#B45309]">
                              <Mail className="h-3.5 w-3.5" /> {l.email}
                            </a>
                            {l.company && (
                              <span className="inline-flex items-center gap-1.5">
                                <Building2 className="h-3.5 w-3.5" /> {l.company}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="mr-1 text-xs text-[#8A8272]">{fmt(l.created_at)}</span>
                        <IconBtn onClick={() => toggleHandled(l)} label="Toggle handled" testId="lead-toggle-handled" title={l.handled ? "Mark as unhandled" : "Mark as handled"}>
                          {l.handled ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Circle className="h-4 w-4" />}
                        </IconBtn>
                        <IconBtn onClick={() => deleteLead(l.id)} label="Delete lead" testId="lead-delete" danger>
                          <Trash2 className="h-4 w-4" />
                        </IconBtn>
                      </div>
                    </div>
                    <p className="mt-4 whitespace-pre-wrap rounded-xl bg-[#FDFBF7] p-4 text-sm leading-relaxed text-[#4A4436]">{l.message}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ---------- BLOGS LIST ---------- */}
          {tab === "blogs" && !editing && (
            <div className="mt-6" data-testid="blogs-admin-list">
              <div className="space-y-4">
                {posts.length === 0 && (
                  <div className="rounded-2xl border border-dashed border-[#D9D0BC] bg-white/60 p-12 text-center">
                    <Newspaper className="mx-auto h-8 w-8 text-[#C9BFA8]" />
                    <p className="mt-3 text-sm font-semibold text-[#5D564A]">No posts yet</p>
                  </div>
                )}
                {posts.map((p) => (
                  <div
                    key={p.id}
                    className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-[#EDE5D3] bg-white p-5 shadow-[0_2px_10px_rgba(31,27,19,0.03)]"
                    data-testid="post-row"
                  >
                    <div className="flex min-w-0 items-center gap-4">
                      {p.thumbnail ? (
                        <img src={p.thumbnail} alt="" className="h-16 w-24 shrink-0 rounded-xl object-cover" />
                      ) : (
                        <div className="flex h-16 w-24 shrink-0 items-center justify-center rounded-xl bg-[#FAF6EE]">
                          <Newspaper className="h-5 w-5 text-[#C9BFA8]" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="truncate font-heading text-base font-semibold text-[#1F1B13]">{p.title}</p>
                          <span
                            className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${
                              p.published ? "bg-emerald-50 text-emerald-600" : "bg-[#F0A322]/15 text-[#B45309]"
                            }`}
                          >
                            {p.published ? "Published" : "Draft"}
                          </span>
                        </div>
                        <p className="mt-1 truncate text-xs text-[#8A8272]">
                          /blogs/{p.slug} · {fmtDate(p.created_at)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {p.published && (
                        <Link to={`/blogs/${p.slug}`} aria-label="View post" title="View on site" className="rounded-full border border-[#EDE5D3] p-2 text-[#8A8272] transition-colors hover:border-[#E8930C] hover:bg-[#F0A322]/10 hover:text-[#B45309]">
                          <ArrowUpRight className="h-4 w-4" />
                        </Link>
                      )}
                      <IconBtn onClick={() => togglePublish(p)} label="Toggle publish" testId="post-toggle-publish" title={p.published ? "Unpublish" : "Publish"}>
                        {p.published ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </IconBtn>
                      <IconBtn onClick={() => setEditing({ ...p })} label="Edit post" testId="post-edit">
                        <Pencil className="h-4 w-4" />
                      </IconBtn>
                      <IconBtn onClick={() => deletePost(p.id)} label="Delete post" testId="post-delete" danger>
                        <Trash2 className="h-4 w-4" />
                      </IconBtn>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ---------- POST EDITOR ---------- */}
          {tab === "blogs" && editing && (
            <form onSubmit={savePost} className="mt-6 rounded-3xl border border-[#EDE5D3] bg-white p-7 shadow-[0_8px_40px_rgba(31,27,19,0.06)] lg:p-9" data-testid="post-editor">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#8A8272]">
                  {editing.id ? `Editing · /blogs/${editing.slug}` : "New article"}
                </p>
                <button type="button" onClick={() => setEditing(null)} aria-label="Close editor" data-testid="editor-close" className="rounded-full border border-[#EDE5D3] p-2 text-[#8A8272] hover:text-[#B45309]">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="mt-6 grid gap-6">
                <div className="space-y-2">
                  <Label className="text-[#4A4436]">Title *</Label>
                  <Input data-testid="editor-title" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="Post title" className={inputCls} />
                </div>
                <div className="grid gap-6 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label className="text-[#4A4436]">Thumbnail URL</Label>
                    <Input data-testid="editor-thumbnail" value={editing.thumbnail} onChange={(e) => setEditing({ ...editing, thumbnail: e.target.value })} placeholder="https://… or /assets/…" className={inputCls} />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#4A4436]">External URL (optional)</Label>
                    <Input data-testid="editor-external" value={editing.external_url} onChange={(e) => setEditing({ ...editing, external_url: e.target.value })} placeholder="Link to article hosted elsewhere" className={inputCls} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#4A4436]">Excerpt</Label>
                  <Textarea data-testid="editor-excerpt" rows={2} value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} placeholder="Short summary shown on the blog card" className={inputCls.replace("h-11 ", "")} />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#4A4436]">Content</Label>
                  <Textarea data-testid="editor-content" rows={12} value={editing.content} onChange={(e) => setEditing({ ...editing, content: e.target.value })} placeholder="Write the article — blank lines create new paragraphs" className={inputCls.replace("h-11 ", "")} />
                </div>
                <div className="flex flex-wrap items-center justify-between gap-4 border-t border-[#F3EDDF] pt-6">
                  <div className="flex items-center gap-3">
                    <Switch id="published" data-testid="editor-published" checked={editing.published} onCheckedChange={(v) => setEditing({ ...editing, published: v })} className="data-[state=checked]:bg-[#E8930C]" />
                    <Label htmlFor="published" className="cursor-pointer text-sm text-[#4A4436]">Published</Label>
                  </div>
                  <div className="flex gap-3">
                    <button type="button" onClick={() => setEditing(null)} className="rounded-full border border-[#D9D0BC] px-7 py-3 text-sm font-semibold text-[#5D564A] hover:text-[#B45309]">
                      Cancel
                    </button>
                    <button type="submit" disabled={saving} data-testid="editor-save" className="inline-flex items-center gap-2 rounded-full bg-[#F0A322] px-7 py-3 text-sm font-semibold text-[#231A07] transition-colors hover:bg-[#E8930C] disabled:opacity-60">
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Post
                    </button>
                  </div>
                </div>
              </div>
            </form>
          )}
        </main>
      </div>
    </div>
  );
};

export default Admin;
